"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.HDSModelPreferred = exports.CERVICAL_FLUID_METHOD_LEGACY_KEY = exports.CERVICAL_FLUID_METHOD_KEY = void 0;
exports.getPreferredInput = getPreferredInput;
exports.getPreferredDisplay = getPreferredDisplay;
const HDSModelInitAndSingleton_ts_1 = require("./HDSModelInitAndSingleton.js");
const HDSSettings_ts_1 = __importDefault(require("../settings/HDSSettings.js"));
const accountPreferences_ts_1 = require("../settings/accountPreferences.js");
const localizeText_ts_1 = require("../localizeText.js");
/**
 * The setting that holds the cervical-fluid charting method a person enters observations in
 * (an HDSSettings dynamic key, value: a converter method id such as `creighton`).
 *
 * Canonical form `preferred-input-<itemKey>`, as every dynamic setting (the settings event's
 * `content.itemKey` is an item key; documented since hds-lib 0.3.0, read by `getPreferredInput`,
 * and the display twin `preferred-display-body-vulva-mucus-inspect` is what apps already write).
 * Write this key; import it rather than spelling it (B-2026-10-26-16).
 */
exports.CERVICAL_FLUID_METHOD_KEY = 'preferred-input-body-vulva-mucus-inspect';
/**
 * Read-only fallback: the converter-model form `preferred-input-cervical-fluid` that hds-forms-js
 * `Convertible` read (and some accounts hold). `getPreferredInput` reads it when the canonical key
 * is not set; never write it.
 */
exports.CERVICAL_FLUID_METHOD_LEGACY_KEY = 'preferred-input-cervical-fluid';
/**
 * HDSModel-Preferred — Unified API for preferred representations.
 *
 * Resolves per-item settings (preferred-input-*, preferred-display-*)
 * with fallback to global unitSystem for variation items.
 *
 * Works for both:
 * - Variation items (body-weight kg/lb, body-height m/ft)
 * - Converter items (mood, cervical-fluid)
 */
class HDSModelPreferred {
    #model;
    constructor(model) {
        this.#model = model;
    }
    /**
     * Get the preferred input config for an item.
     * Used by form components to determine what input UI to show.
     */
    getPreferredInput(itemKey) {
        return this.#resolve(itemKey, 'preferred-input-');
    }
    /**
     * Get the preferred display config for an item.
     * Used by eventToShortText and display components.
     */
    getPreferredDisplay(itemKey) {
        return this.#resolve(itemKey, 'preferred-display-');
    }
    #resolve(itemKey, settingPrefix) {
        const itemDef = this.#model.itemsDefs.forKey(itemKey, false);
        if (!itemDef)
            return { type: 'default' };
        const data = itemDef.data;
        const perItemSetting = HDSSettings_ts_1.default.isHooked
            ? HDSSettings_ts_1.default.get(`${settingPrefix}${itemKey}`)
            : undefined;
        // ── Variation items ──
        if (data.variations?.eventType) {
            const options = data.variations.eventType.options || [];
            let selectedEventType;
            // 1. Per-item override
            if (perItemSetting && typeof perItemSetting === 'string') {
                const match = options.find((o) => o.value === perItemSetting);
                if (match)
                    selectedEventType = perItemSetting;
            }
            // 2. Fallback to unitSystem (account-level when set, else the app's own)
            if (!selectedEventType && (0, accountPreferences_ts_1.hasAccountPreference)('unitSystem')) {
                const system = (0, accountPreferences_ts_1.resolveAccountPreference)('unitSystem');
                selectedEventType = this.#resolveVariationFromUnitSystem(data.variations.eventType, system);
            }
            // 3. Default: first option
            if (!selectedEventType) {
                selectedEventType = options[0]?.value;
            }
            const opt = options.find((o) => o.value === selectedEventType);
            const symbol = this.#getSymbol(selectedEventType);
            const optLabel = opt?.label ? ((0, localizeText_ts_1.localizeText)(opt.label) || undefined) : undefined;
            return {
                type: 'variation',
                eventType: selectedEventType,
                symbol: symbol || optLabel,
            };
        }
        // ── Converter items ──
        if (data['converter-engine']) {
            const ce = data['converter-engine'];
            const converterItemKey = ce.models;
            const engine = this.#model.converters.getEngine(converterItemKey);
            // Input only: fall back to the converter-model form (`preferred-input-cervical-fluid`) that
            // hds-forms-js `Convertible` read, so a method saved under it is still honoured (B-2026-10-26-16).
            let setting = perItemSetting;
            const hasPerItem = typeof setting === 'string' && setting !== '';
            if (!hasPerItem && settingPrefix === 'preferred-input-' && HDSSettings_ts_1.default.isHooked) {
                setting = HDSSettings_ts_1.default.get(`preferred-input-${converterItemKey}`);
            }
            if (setting && typeof setting === 'string') {
                const methodDef = engine?.getMethodDef(setting);
                return {
                    type: 'converter',
                    method: setting,
                    methodName: methodDef?.name ? ((0, localizeText_ts_1.localizeText)(methodDef.name) || setting) : setting,
                    engine,
                };
            }
            return {
                type: 'converter',
                method: null,
                engine,
            };
        }
        return { type: 'default' };
    }
    /**
     * Resolve variation eventType from unitSystem setting.
     * Uses the model's conversions data to find which eventType belongs to which unit system.
     */
    #resolveVariationFromUnitSystem(variationDef, system) {
        const options = variationDef.options || [];
        const conversions = this.#model.modelData.conversions;
        if (!conversions)
            return undefined;
        for (const opt of options) {
            const eventType = opt.value;
            const slash = eventType.indexOf('/');
            if (slash < 0)
                continue;
            const category = eventType.substring(0, slash);
            const unit = eventType.substring(slash + 1);
            const catDef = conversions[category];
            if (catDef && catDef[system] === unit) {
                return eventType;
            }
        }
        return undefined;
    }
    #getSymbol(eventType) {
        if (!eventType)
            return undefined;
        try {
            return this.#model.eventTypes.getEventTypeSymbol(eventType) || undefined;
        }
        catch {
            return undefined;
        }
    }
}
exports.HDSModelPreferred = HDSModelPreferred;
// ─── Standalone functions using the model singleton ────────────────────────
let _preferred = null;
function getPreferred() {
    const model = (0, HDSModelInitAndSingleton_ts_1.getModel)();
    if (!model.isLoaded)
        throw new Error('Model not loaded');
    if (!_preferred)
        _preferred = new HDSModelPreferred(model);
    return _preferred;
}
/** Get the preferred input config for an item (standalone, uses singleton model) */
function getPreferredInput(itemKey) {
    return getPreferred().getPreferredInput(itemKey);
}
/** Get the preferred display config for an item (standalone, uses singleton model) */
function getPreferredDisplay(itemKey) {
    return getPreferred().getPreferredDisplay(itemKey);
}
